import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Group,
  Stack,
  Text,
  Button,
  Table,
  Badge,
  ActionIcon,
  Modal,
  Pagination,
  Select,
  TextInput,
  Checkbox,
  Grid,
  Title,
  Alert,
  LoadingOverlay,
  Tooltip,
  NumberInput,
  Textarea,
  Divider,
} from "@mantine/core";
import {
  IconEye,
  IconFileText,
  IconPlus,
  IconX,
  IconSearch,
  IconRefresh,
  IconCash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { DatePickerInput } from "@mantine/dates";
import {
  getAllInvoicesAdmin,
  generateInvoiceAdmin,
  updateInvoiceStatusAdmin,
  markInvoiceAsPaidAdmin,
  cancelInvoiceAdmin,
  bulkGenerateInvoicesAdmin,
  getInvoiceDetails,
} from "../misc/api";

const AdminInvoiceScreen = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Modals
  const [invoiceDetailModal, setInvoiceDetailModal] = useState(false);
  const [generateInvoiceModal, setGenerateInvoiceModal] = useState(false);
  const [markPaidModal, setMarkPaidModal] = useState(false);
  
  // Form states
  const [generateForm, setGenerateForm] = useState({
    transaction_id: "",
    send_email: true,
    regenerate: false,
  });
  
  const [paymentForm, setPaymentForm] = useState({
    payment_method: "cash",
    payment_reference: "",
    notes: "",
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Filters
  const [filters, setFilters] = useState({
    status: "",
    user_id: "",
    start_date: null,
    end_date: null,
    search: "",
  });

  useEffect(() => {
    loadInvoices();
  }, [pagination.page, filters]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await getAllInvoicesAdmin(
        pagination.page,
        pagination.limit,
        filters
      );

      if (response.success) {
        setInvoices(response.data);
        setPagination(response.pagination);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load invoices",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setLoading(true);
      const response = await generateInvoiceAdmin(
        parseInt(generateForm.transaction_id),
        {
          send_email: generateForm.send_email,
          regenerate: generateForm.regenerate,
        }
      );

      if (response.success) {
        notifications.show({
          title: "Success",
          message: "Invoice generated successfully",
          color: "green",
        });
        setGenerateInvoiceModal(false);
        setGenerateForm({
          transaction_id: "",
          send_email: true,
          regenerate: false,
        });
        loadInvoices();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to generate invoice",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    try {
      setLoading(true);
      const response = await markInvoiceAsPaidAdmin(invoice.invoice_id, {
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference,
        notes: paymentForm.notes,
      });

      if (response.success) {
        notifications.show({
          title: "Success",
          message: "Invoice marked as paid",
          color: "green",
        });
        setMarkPaidModal(false);
        setPaymentForm({
          payment_method: "cash",
          payment_reference: "",
          notes: "",
        });
        loadInvoices();
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to mark invoice as paid",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (invoice, newStatus) => {
    modals.openConfirmModal({
      title: `Update Invoice Status`,
      children: (
        <Text size="sm">
          Are you sure you want to change the status of invoice{" "}
          {invoice.invoice_number} to {newStatus.toUpperCase()}?
        </Text>
      ),
      labels: { confirm: "Update", cancel: "Cancel" },
      onConfirm: async () => {
        try {
          const response = await updateInvoiceStatusAdmin(
            invoice.invoice_id,
            newStatus
          );

          if (response.success) {
            notifications.show({
              title: "Success",
              message: "Invoice status updated",
              color: "green",
            });
            loadInvoices();
          } else {
            throw new Error(response.error);
          }
        } catch (error) {
          notifications.show({
            title: "Error",
            message: error.message || "Failed to update status",
            color: "red",
          });
        }
      },
    });
  };

  const handleCancelInvoice = (invoice) => {
    modals.openPromptModal({
      title: "Cancel Invoice",
      children: (
        <Text size="sm">
          Please provide a reason for cancelling invoice {invoice.invoice_number}:
        </Text>
      ),
      labels: { confirm: "Cancel Invoice", cancel: "Keep Invoice" },
      placeholder: "Cancellation reason...",
      onConfirm: async (reason) => {
        try {
          const response = await cancelInvoiceAdmin(invoice.invoice_id, reason);

          if (response.success) {
            notifications.show({
              title: "Success",
              message: "Invoice cancelled",
              color: "green",
            });
            loadInvoices();
          } else {
            throw new Error(response.error);
          }
        } catch (error) {
          notifications.show({
            title: "Error",
            message: error.message || "Failed to cancel invoice",
            color: "red",
          });
        }
      },
    });
  };

  const handleBulkGenerate = () => {
    modals.openConfirmModal({
      title: "Bulk Generate Invoices",
      children: (
        <Stack spacing="md">
          <Text size="sm">
            Generate invoices for {selectedInvoices.length} selected transactions?
          </Text>
          <Checkbox
            label="Send email notifications"
            defaultChecked={true}
            id="bulk-email"
          />
        </Stack>
      ),
      labels: { confirm: "Generate", cancel: "Cancel" },
      onConfirm: async () => {
        const sendEmails = document.getElementById("bulk-email").checked;
        
        try {
          setLoading(true);
          const response = await bulkGenerateInvoicesAdmin(
            selectedInvoices,
            sendEmails
          );

          if (response.success) {
            const successCount = response.data.filter(r => r.success).length;
            const failCount = response.data.filter(r => !r.success).length;
            
            notifications.show({
              title: "Bulk Generation Complete",
              message: `${successCount} invoices generated successfully. ${failCount} failed.`,
              color: successCount > 0 ? "green" : "red",
            });
            
            setSelectedInvoices([]);
            loadInvoices();
          } else {
            throw new Error(response.error);
          }
        } catch (error) {
          notifications.show({
            title: "Error",
            message: error.message || "Failed to bulk generate invoices",
            color: "red",
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const viewInvoiceDetails = async (invoiceId) => {
    try {
      setLoading(true);
      const response = await getInvoiceDetails(invoiceId);

      if (response.success) {
        setSelectedInvoice(response.data);
        setInvoiceDetailModal(true);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load invoice details",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "paid": return "green";
      case "issued": return "blue";
      case "overdue": return "red";
      case "cancelled": return "gray";
      default: return "gray";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleInvoiceSelection = (invoiceId) => {
    setSelectedInvoices((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedInvoices.length === invoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoices.map((inv) => inv.invoice_id));
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack spacing="lg">
        {/* Header */}
        <Group justify="space-between">
          <Title>Invoice Management</Title>
          <Group>
            <Button
              leftSection={<IconRefresh size="1rem" />}
              variant="light"
              onClick={loadInvoices}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              leftSection={<IconPlus size="1rem" />}
              onClick={() => setGenerateInvoiceModal(true)}
            >
              Generate Invoice
            </Button>
          </Group>
        </Group>

        {/* Filters */}
        <Card withBorder padding="md">
          <Grid>
            <Grid.Col span={3}>
              <TextInput
                placeholder="Search invoices..."
                leftSection={<IconSearch size="1rem" />}
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <Select
                placeholder="Status"
                data={[
                  { value: "", label: "All Statuses" },
                  { value: "issued", label: "Issued" },
                  { value: "paid", label: "Paid" },
                  { value: "overdue", label: "Overdue" },
                  { value: "cancelled", label: "Cancelled" },
                ]}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value || "")}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInput
                placeholder="User ID"
                value={filters.user_id}
                onChange={(e) => handleFilterChange("user_id", e.target.value)}
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DatePickerInput
                placeholder="Start Date"
                value={filters.start_date}
                onChange={(value) => handleFilterChange("start_date", value)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <DatePickerInput
                placeholder="End Date"
                value={filters.end_date}
                onChange={(value) => handleFilterChange("end_date", value)}
                clearable
              />
            </Grid.Col>
            <Grid.Col span={1}>
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({
                    status: "",
                    user_id: "",
                    start_date: null,
                    end_date: null,
                    search: "",
                  });
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                Clear
              </Button>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Bulk Actions */}
        {selectedInvoices.length > 0 && (
          <Alert color="blue">
            <Group justify="space-between">
              <Text>
                {selectedInvoices.length} invoice(s) selected
              </Text>
              <Group>
                <Button size="xs" onClick={handleBulkGenerate}>
                  Bulk Generate
                </Button>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setSelectedInvoices([])}
                >
                  Clear Selection
                </Button>
              </Group>
            </Group>
          </Alert>
        )}

        {/* Invoices Table */}
        <Card withBorder padding={0}>
          <LoadingOverlay visible={loading} />
          
          {invoices.length === 0 && !loading ? (
            <Stack align="center" py="xl" spacing="md">
              <IconFileText size="3rem" color="gray" />
              <Text c="dimmed">No invoices found</Text>
            </Stack>
          ) : (
            <>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      <Checkbox
                        checked={
                          selectedInvoices.length === invoices.length &&
                          invoices.length > 0
                        }
                        indeterminate={
                          selectedInvoices.length > 0 &&
                          selectedInvoices.length < invoices.length
                        }
                        onChange={toggleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th>Invoice #</Table.Th>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Description</Table.Th>
                    <Table.Th>Issue Date</Table.Th>
                    <Table.Th>Due Date</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {invoices.map((invoice) => (
                    <Table.Tr key={invoice.invoice_id}>
                      <Table.Td>
                        <Checkbox
                          checked={selectedInvoices.includes(invoice.invoice_id)}
                          onChange={() =>
                            toggleInvoiceSelection(invoice.invoice_id)
                          }
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {invoice.invoice_number}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {invoice.user_name || invoice.billing_name || "N/A"}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {invoice.user_email || invoice.billing_email}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2}>
                          {invoice.description}
                        </Text>
                      </Table.Td>
                      <Table.Td>{formatDate(invoice.issue_date)}</Table.Td>
                      <Table.Td>{formatDate(invoice.due_date)}</Table.Td>
                      <Table.Td>
                        <Text fw={500}>{formatCurrency(invoice.amount)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getStatusColor(invoice.status)}>
                          {invoice.status.toUpperCase()}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group spacing="xs">
                          <Tooltip label="View Details">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => viewInvoiceDetails(invoice.invoice_id)}
                            >
                              <IconEye size="1rem" />
                            </ActionIcon>
                          </Tooltip>
                          
                          {invoice.status === "issued" && (
                            <Tooltip label="Mark as Paid">
                              <ActionIcon
                                variant="subtle"
                                color="green"
                                onClick={() => {
                                  setSelectedInvoice(invoice);
                                  setMarkPaidModal(true);
                                }}
                              >
                                <IconCash size="1rem" />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          
                          {invoice.status !== "cancelled" && (
                            <Tooltip label="Cancel Invoice">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleCancelInvoice(invoice)}
                              >
                                <IconX size="1rem" />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <Pagination
                    value={pagination.page}
                    onChange={(page) =>
                      setPagination((prev) => ({ ...prev, page }))
                    }
                    total={pagination.pages}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </Stack>

      {/* Generate Invoice Modal */}
      <Modal
        opened={generateInvoiceModal}
        onClose={() => setGenerateInvoiceModal(false)}
        title="Generate Invoice"
      >
        <Stack spacing="md">
          <NumberInput
            label="Transaction ID"
            placeholder="Enter transaction ID"
            value={generateForm.transaction_id}
            onChange={(value) =>
              setGenerateForm((prev) => ({ ...prev, transaction_id: value }))
            }
            required
          />
          <Checkbox
            label="Send email notification"
            checked={generateForm.send_email}
            onChange={(event) =>
              setGenerateForm((prev) => ({
                ...prev,
                send_email: event.currentTarget?.checked,
              }))
            }
          />
          <Checkbox
            label="Regenerate if exists"
            checked={generateForm.regenerate}
            onChange={(event) =>
              setGenerateForm((prev) => ({
                ...prev,
                regenerate: event.currentTarge?.checked,
              }))
            }
          />
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => setGenerateInvoiceModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleGenerateInvoice} loading={loading}>
              Generate Invoice
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Mark as Paid Modal */}
      <Modal
        opened={markPaidModal}
        onClose={() => setMarkPaidModal(false)}
        title="Mark Invoice as Paid"
      >
        <Stack spacing="md">
          <Text size="sm">
            Marking invoice {selectedInvoice?.invoice_number} as paid
          </Text>
          <Select
            label="Payment Method"
            data={[
              { value: "cash", label: "Cash" },
              { value: "card", label: "Card" },
              { value: "bank_transfer", label: "Bank Transfer" },
              { value: "tikitaka", label: "TikiTaka Terminal" },
            ]}
            value={paymentForm.payment_method}
            onChange={(value) =>
              setPaymentForm((prev) => ({ ...prev, payment_method: value }))
            }
            required
          />
          <TextInput
            label="Payment Reference"
            placeholder="Transaction reference or receipt number"
            value={paymentForm.payment_reference}
            onChange={(e) =>
              setPaymentForm((prev) => ({
                ...prev,
                payment_reference: e.target.value,
              }))
            }
          />
          <Textarea
            label="Notes"
            placeholder="Additional notes about the payment"
            value={paymentForm.notes}
            onChange={(e) =>
              setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))
            }
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setMarkPaidModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleMarkAsPaid(selectedInvoice)}
              loading={loading}
            >
              Mark as Paid
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Invoice Details Modal (reuse from user invoice screen) */}
      {selectedInvoice && (
        <Modal
          opened={invoiceDetailModal}
          onClose={() => setInvoiceDetailModal(false)}
          title={`Invoice ${selectedInvoice.invoice_number}`}
          size="lg"
        >
          {/* Invoice details content - similar to user screen but with admin actions */}
          <Stack spacing="md">
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">Invoice Number</Text>
                <Text fw={500}>{selectedInvoice.invoice_number}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">Status</Text>
                <Badge color={getStatusColor(selectedInvoice.status)}>
                  {selectedInvoice.status.toUpperCase()}
                </Badge>
              </Grid.Col>
            </Grid>
            
            <Divider />
            
            <Grid>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Amount</Text>
                <Text size="lg" fw={700}>
                  {formatCurrency(selectedInvoice.amount)}
                </Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Issue Date</Text>
                <Text>{formatDate(selectedInvoice.issue_date)}</Text>
              </Grid.Col>
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">Due Date</Text>
                <Text>{formatDate(selectedInvoice.due_date)}</Text>
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              {selectedInvoice.status === "issued" && (
                <Button
                  color="green"
                  leftSection={<IconCash size="1rem" />}
                  onClick={() => setMarkPaidModal(true)}
                >
                  Mark as Paid
                </Button>
              )}
              {selectedInvoice.status !== "cancelled" && (
                <Button
                  color="red"
                  variant="outline"
                  leftSection={<IconX size="1rem" />}
                  onClick={() => handleCancelInvoice(selectedInvoice)}
                >
                  Cancel Invoice
                </Button>
              )}
            </Group>
          </Stack>
        </Modal>
      )}
    </Container>
  );
};

export default AdminInvoiceScreen;